import { useEffect, useState } from "react";
import api from "../../services/api";
import { cachedGet } from "../../services/apiCache";
import sal from "sal.js";

import Banner from "./partials/Banner";
import ButtonsSection from "./partials/ButtonsSection";
import VideoSection from "./partials/VideoSection";
import AboutSection from "./partials/AboutSection";
import ProgressSection from "./partials/ProgressSection";
import CareSection from "./partials/CareSection";
import SpecialitySection from "./partials/SpecialitySection";
import DoctorsSection from "./partials/DoctorsSection";
import HealingSection from "./partials/HealingSection";
import HealthCheckups from "./partials/HealthCheckups";
import TestimonialSection from "./partials/TestimonialSection";
import LatestNewsSection from "./partials/LatestNewsSection";
import LatestNewsItems from "./partials/LatestNewsItems";
import VideosSection from "./partials/VideosSection";
import BlogSection from "./partials/BlogSection";
import FaqSection from "./partials/FaqSection";
import MapSection from "./partials/MapSection";

import HeroPoster from "./partials/HeroPoster";

// const VideoSectionSkeleton = () => (
//   <section className="gastro-care-banner video-banner">
//     <div className="container">
//       <div className="text-content skeleton">
//         <div className="skeleton-line long shimmer"></div>
//         <div className="skeleton-line medium shimmer"></div>
//         <div className="skeleton-btn-group mt-4">
//           <div className="skeleton-btn shimmer"></div>
//           <div className="skeleton-btn shimmer"></div>
//         </div>
//       </div>
//     </div>
//   </section>
// );

const BannerSkeleton = () => (
  <section className="banner-section">
    <div
      className="skeleton shimmer"
      style={{ height: "420px", borderRadius: "0" }}
    />
  </section>
);

const Home = () => {
  const [doctors, setDoctors] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [newsEvents, setNewsEvents] = useState([]);
  const [homeContent, setHomeContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const controller = new AbortController();

    const fetchCriticalData = async () => {
      try {
        const homeRes = await cachedGet("home", () =>
          api.get("/api/pages/home", { signal: controller.signal })
        );

        setHomeContent(homeRes.data);
        setLoading(false);

        const initSal = () => sal({ threshold: 0.2, once: true });

        if ("requestIdleCallback" in window) {
          requestIdleCallback(initSal);
        } else {
          setTimeout(initSal, 1500);
        }
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Critical API error:", err);
          setError("Failed to load home content");
          setLoading(false);
        }
      }
    };

    fetchCriticalData();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (loading) return;

    const controller = new AbortController();

    const fetchNonCriticalData = async () => {
      try {
        const results = await Promise.allSettled([
          api.get("/api/news-events", { signal: controller.signal }),
          api.get("/api/doctors/screen/home", { signal: controller.signal }),
          api.get("/api/testimonials/screen/home", { signal: controller.signal }),
          api.get("/api/faqs/screen/home", { signal: controller.signal }),
        ]);

        const [newsRes, doctorsRes, testimonialsRes, faqsRes] = results;

        if (newsRes.status === "fulfilled")
          setNewsEvents(newsRes.value.data);

        if (doctorsRes.status === "fulfilled")
          setDoctors(doctorsRes.value.data);

        if (testimonialsRes.status === "fulfilled")
          setTestimonials(testimonialsRes.value.data);

        if (faqsRes.status === "fulfilled")
          setFaqs(faqsRes.value.data);
      } catch (err) {
        console.warn("Non-critical API issue:", err);
      }
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(fetchNonCriticalData);
    } else {
      setTimeout(fetchNonCriticalData, 300);
    }

    return () => controller.abort();
  }, [loading]);

  if (error) return null;

  const bannerData = homeContent.banner_data || {};
  const videoCarouselData = homeContent.video_carousel_content || {};
  const videoData = homeContent.video_data || {};
  const videoItems = videoData.items || [];

  return (
    <>
      {loading ? (
        <HeroPoster />
      ) : (
        <VideoSection videoItems={videoCarouselData.items} />
      )}

      {loading || !bannerData?.desktop_images?.length ? (
        <BannerSkeleton />
      ) : (
        <Banner
          desktopImages={bannerData.desktop_images}
          mobileImages={bannerData.mobile_images}
        />
      )}

      <ButtonsSection />
      <AboutSection />
      <ProgressSection />
      <CareSection />
      <SpecialitySection />

      {doctors.length > 0 && <DoctorsSection doctors={doctors} />}

      <HealingSection />
      <HealthCheckups />

      {testimonials?.items?.length > 0 && (
        <TestimonialSection testimonials={testimonials} />
      )}

      <LatestNewsSection />

      {newsEvents.length > 0 && (
        <LatestNewsItems newsItems={newsEvents} />
      )}

      {videoItems.length > 0 && (
        <VideosSection videoData={videoData} />
      )}

      <BlogSection />

      {faqs.length > 0 && <FaqSection faqs={faqs} />}

      <MapSection />
    </>
  );
};

export default Home;
